import React from 'react';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import Alert from '@mui/material/Alert';
import Divider from '@mui/material/Divider';
import ttsStepsService from '../service/story-image-plan-tts-steps.service';
import { StoryImagePlanTtsStepEntity, TtsSpeaker } from '../types/entities';
import storyImagePlanService from '../service/story-image-plan.service';

export type TtsStepsFormProps = {
  imagePlanId: string;
};

const TtsStepsForm: React.FC<TtsStepsFormProps> = ({ imagePlanId }) => {
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [items, setItems] = React.useState<StoryImagePlanTtsStepEntity[]>([]);
  const [measuringId, setMeasuringId] = React.useState<string | null>(null);

  const [newSpeaker, setNewSpeaker] = React.useState<TtsSpeaker | ''>('');
  const [newText, setNewText] = React.useState('');

  const load = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const plan = await storyImagePlanService.getPlanById(imagePlanId);
      const list = (Array.isArray((plan as any)?.ttsSteps) ? (plan as any).ttsSteps : []) as StoryImagePlanTtsStepEntity[];
      setItems(list || []);
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'TTS adımları yüklenemedi');
    } finally {
      setLoading(false);
    }
  }, [imagePlanId]);

  React.useEffect(() => { load(); }, [load]);

  const handleAdd = async () => {
    if (!newText.trim()) return;
    setLoading(true);
    try {
      await ttsStepsService.createStep({ imagePlanId, orderIndex: (items[items.length-1]?.orderIndex ?? 0) + 1, text: newText, speaker: newSpeaker || null });
      setNewText('');
      setNewSpeaker('');
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Ekleme başarısız');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async (it: StoryImagePlanTtsStepEntity) => {
    setLoading(true);
    try {
      await ttsStepsService.updateStep(it.id, { text: it.text, speaker: it.speaker ?? null, language: it.language ?? null, subtitleChunkMaxWords: it.subtitleChunkMaxWords ?? null, subtitleChunkMaxChars: it.subtitleChunkMaxChars ?? null, audioDurationMs: typeof it.audioDurationMs === 'number' ? it.audioDurationMs : null });
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Güncelleme başarısız');
    } finally {
      setLoading(false);
    }
  };

  // delete handler removed (unused)

  const measureDuration = async (it: StoryImagePlanTtsStepEntity) => {
    if (!it.audioUrl) return;
    setMeasuringId(it.id);
    try {
      const audio = new Audio();
      audio.src = it.audioUrl;
      await new Promise<void>((resolve, reject) => {
        const onLoaded = () => resolve();
        const onError = () => reject(new Error('metadata yüklenemedi'));
        audio.addEventListener('loadedmetadata', onLoaded, { once: true });
        audio.addEventListener('error', onError, { once: true });
      });
      const ms = Math.round((audio.duration || 0) * 1000);
      setItems((prev) => prev.map((x) => x.id === it.id ? { ...x, audioDurationMs: ms } : x));
    } catch (e) {
      setError('Süre ölçümü başarısız');
    } finally {
      setMeasuringId(null);
    }
  };

  const move = async (idx: number, dir: -1 | 1) => {
    const next = [...items];
    const j = idx + dir;
    if (j < 0 || j >= next.length) return;
    const tmp = next[idx];
    next[idx] = next[j];
    next[j] = tmp;
    // recalc orderIndex as 1..N
    const payload = next.map((r, i) => ({ id: r.id, orderIndex: i + 1 }));
    setItems(next);
    try {
      await ttsStepsService.reorderSteps(imagePlanId, payload);
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Sıralama başarısız');
      // ignore
    }
  };

  return (
    <Stack spacing={1.5}>
      <Typography variant="subtitle2">TTS Steps</Typography>
      {error && <Alert severity="error">{error}</Alert>}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
        <TextField select label="Speaker" value={newSpeaker} onChange={(e) => setNewSpeaker(e.target.value as any)} sx={{ minWidth: 160 }}>
          <MenuItem value="">(none)</MenuItem>
          <MenuItem value="A">A</MenuItem>
          <MenuItem value="B">B</MenuItem>
          <MenuItem value="C">C</MenuItem>
        </TextField>
        <TextField label="Text" value={newText} onChange={(e) => setNewText(e.target.value)} fullWidth />
        <Button variant="contained" onClick={handleAdd} disabled={loading || !newText.trim()}>Ekle</Button>
      </Stack>
      <Divider />
      <Stack spacing={1}>
        {items.map((it, idx) => (
          <Stack key={it.id} spacing={1} sx={{ p: 1, border: '1px solid rgba(0,0,0,0.1)', borderRadius: 1 }}>
            <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
              <Typography variant="caption" color="text.secondary">#{it.orderIndex}</Typography>
              <Stack direction="row" spacing={0.5}>
                <IconButton size="small" onClick={() => move(idx, -1)} disabled={idx === 0}><ArrowUpwardIcon fontSize="small" /></IconButton>
                <IconButton size="small" onClick={() => move(idx, +1)} disabled={idx === items.length - 1}><ArrowDownwardIcon fontSize="small" /></IconButton>
              </Stack>
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
              <TextField select label="Speaker" value={it.speaker ?? ''} onChange={(e) => setItems((prev) => prev.map((x) => x.id === it.id ? { ...x, speaker: e.target.value as any } : x))} sx={{ minWidth: 160 }}>
                <MenuItem value="">(none)</MenuItem>
                <MenuItem value="A">A</MenuItem>
                <MenuItem value="B">B</MenuItem>
                <MenuItem value="C">C</MenuItem>
              </TextField>
              <TextField label="Language (e.g. tr-TR)" value={it.language ?? ''} onChange={(e) => setItems((prev) => prev.map((x) => x.id === it.id ? { ...x, language: e.target.value } : x))} sx={{ minWidth: 180 }} />
              <TextField label="Max Words" type="number" value={it.subtitleChunkMaxWords ?? ''} onChange={(e) => setItems((prev) => prev.map((x) => x.id === it.id ? { ...x, subtitleChunkMaxWords: e.target.value ? Number(e.target.value) : null } : x))} sx={{ minWidth: 140 }} />
              <TextField label="Max Chars" type="number" value={it.subtitleChunkMaxChars ?? ''} onChange={(e) => setItems((prev) => prev.map((x) => x.id === it.id ? { ...x, subtitleChunkMaxChars: e.target.value ? Number(e.target.value) : null } : x))} sx={{ minWidth: 140 }} />
              <TextField label="Audio Duration (ms)" type="number" value={it.audioDurationMs ?? ''} onChange={(e) => setItems((prev) => prev.map((x) => x.id === it.id ? { ...x, audioDurationMs: e.target.value ? Number(e.target.value) : null } : x))} sx={{ minWidth: 180 }} />
              <Button variant="outlined" onClick={() => measureDuration(it)} disabled={!it.audioUrl || measuringId === it.id}>Süreyi Ölç</Button>
            </Stack>
            <TextField label="Text" value={it.text} onChange={(e) => setItems((prev) => prev.map((x) => x.id === it.id ? { ...x, text: e.target.value } : x))} fullWidth multiline minRows={2} />
            <Typography variant="body2">
              Süre:
              <strong>
                {typeof it.audioDurationMs === 'number'
                  ? (() => {
                      const ms = it.audioDurationMs || 0;
                      const sec = ms / 1000;
                      const label = `${sec.toFixed(3)} sn (${ms} ms)`;
                      return ` ${label}`;
                    })()
                  : ' —'}
              </strong>
              {it.audioUrl ? '' : ' (audio yok)'}
            </Typography>
            <Stack direction="row" spacing={1}>
              <Button variant="outlined" onClick={() => handleUpdate(it)} disabled={loading}>Güncelle</Button>
            </Stack>
          </Stack>
        ))}
      </Stack>
    </Stack>
  );
};

export default TtsStepsForm;
